import type { CacheEfficiency } from '@ace-tracker/shared';

/**
 * Cache read vs. cache creation token volume for Claude Code — modeled on
 * ContextWeightChart's two-bar layout (Copilot has no cache concept, so
 * there is no equivalent chart to share this with).
 */
export function CacheEfficiencyChart({ cacheEfficiency }: { cacheEfficiency: CacheEfficiency }) {
  const max = Math.max(cacheEfficiency.cacheCreationTokens, cacheEfficiency.cacheReadTokens, 1);
  const hasVolume = cacheEfficiency.cacheCreationTokens + cacheEfficiency.cacheReadTokens > 0;

  return (
    <div className="flex h-full flex-col justify-center">
      <div className="grid gap-5">
        <Row label="Cache created (new context written)" value={cacheEfficiency.cacheCreationTokens} max={max} colorClassName="bg-primary-blue" />
        <Row label="Cache read (context reused)" value={cacheEfficiency.cacheReadTokens} max={max} colorClassName="bg-success" />
      </div>
      {hasVolume && (
        <p className="mt-5 border-t border-border pt-3 text-xs text-text-muted">
          <span className="font-semibold text-text-default">{Math.round(cacheEfficiency.cacheHitRatio * 100)}%</span> of
          cache-eligible tokens were reused rather than rewritten — a higher share means cheaper, faster turns.
        </p>
      )}
    </div>
  );
}

function Row({ label, value, max, colorClassName }: { label: string; value: number; max: number; colorClassName: string }) {
  const widthPercent = Math.max((value / max) * 100, value > 0 ? 0.6 : 0);
  const fitsInside = widthPercent > 22;
  return (
    <div>
      <p className="mb-1.5 text-[11px] uppercase tracking-wide text-text-faint">{label}</p>
      <div className="relative h-9">
        <div className={`absolute inset-y-0 left-0 rounded ${colorClassName}`} style={{ width: `${widthPercent}%` }} />
        <span
          className={`absolute inset-y-0 flex items-center whitespace-nowrap text-sm font-bold tabular-nums ${
            fitsInside ? 'left-0 px-3 text-text-inverse' : 'text-text-default'
          }`}
          style={fitsInside ? undefined : { left: `calc(${widthPercent}% + 10px)` }}
        >
          {value.toLocaleString()}
        </span>
      </div>
    </div>
  );
}
